import {
  GRID_LINE_COLOR,
  GRID_LINE_THICKNESS
} from "@gettactic/map-builder/src/components/map/defines";
import type { GridConfig } from "@gettactic/map-builder/src/components/map/editor/toolbars/GridPrompt";
import { generateNextSpotLabels } from "@gettactic/map-builder/src/components/map/helpers/generateNextSpotLabels";
import {
  type SpotShape,
  SpotShapeType,
  type WallPathShape
} from "@gettactic/map-builder/src/types";
import {
  computeGridCells,
  computeGridLines
} from "@gettactic/map-builder/src/utils/gridLayout";
import {
  createNodeShape,
  createWallPath
} from "@gettactic/map-builder/src/utils/shapeFactory";
import { normalizeRect } from "../document/bounds";
import { isPrimaryButton } from "../gesture/pointer";
import type { EditorStore } from "../store/createEditorStore";
import type { Point, Rect } from "../types";
import { createFrameScheduler, type Tool, type ToolContext } from "./Tool";

const MIN_SIZE = 4;

/** Drag a rectangle; the chrome's grid prompt then asks for rows, columns
 *  and spot type and `applyGridConfig` paints the lines and spots. */
export const createGridTool = (): Tool => {
  let origin: Point | null = null;
  let context: ToolContext | null = null;

  const frame = createFrameScheduler<Point>((client) => {
    if (!context || !origin) return;
    const point = context.snap.snapPoint(context.toWorld(client));
    context.overlay.set({
      draft: { kind: "zone", rect: normalizeRect(origin, point) }
    });
  });

  const reset = () => {
    frame.cancel();
    origin = null;
    context?.overlay.reset();
  };

  return {
    kind: "grid",
    cursor: "crosshair",
    isBusy: () => origin !== null,
    onPointerDown(toolContext, event) {
      context = toolContext;
      if (!isPrimaryButton(event)) return;
      origin = toolContext.snap.snapPoint(
        toolContext.toWorld({ x: event.clientX, y: event.clientY })
      );
    },
    onPointerMove(toolContext, event) {
      context = toolContext;
      if (!origin) return;
      frame.schedule({ x: event.clientX, y: event.clientY });
    },
    onPointerUp(toolContext, event) {
      context = toolContext;
      if (!origin) return;
      const point = toolContext.snap.snapPoint(
        toolContext.toWorld({ x: event.clientX, y: event.clientY })
      );
      const rect = normalizeRect(origin, point);
      reset();
      // Too small to be a grid: an accidental click, not a prompt.
      if (rect.width <= MIN_SIZE || rect.height <= MIN_SIZE) return;
      toolContext.store.getState().setChrome({ pendingGrid: rect });
    },
    onKeyDown(toolContext, event) {
      if (event.key === "Escape" && origin) {
        context = toolContext;
        reset();
        return true;
      }
      return false;
    },
    cancel(toolContext) {
      context = toolContext;
      reset();
    }
  };
};

/**
 * Paints the grid the prompt configured: one thin wall path per row and
 * column boundary (excluded from floor fill) and, optionally, one spot per
 * cell whose whole cell is its availability area. Same shapes as the Konva
 * editor's `confirmGrid`.
 */
export const applyGridConfig = (
  store: EditorStore,
  rect: Rect,
  config: GridConfig
): void => {
  const state = store.getState();
  const layout = {
    rows: config.rows,
    columns: config.columns,
    gap: config.gap
  };
  const lines = computeGridLines(rect, layout);
  if (lines.length === 0) {
    state.setChrome({ pendingGrid: null });
    state.setTool("select");
    return;
  }
  const lineShapes: WallPathShape[] = lines.map((line) => {
    const path = createWallPath(line.vertices) as WallPathShape;
    path.thickness = GRID_LINE_THICKNESS;
    path.color = GRID_LINE_COLOR;
    path.excludeFromFloorFill = true;
    return path;
  });

  let label = state.meta.lastSpotLabel ?? [];
  const spotShapes: SpotShape[] = config.addSpots
    ? computeGridCells(rect, layout).map((cell) => {
        label =
          generateNextSpotLabels(state.meta.spotLabelPattern, label) ?? label;
        const shape = createNodeShape({
          x: cell.x,
          y: cell.y,
          text: label.join("")
        }) as SpotShape;
        shape.width = cell.width;
        shape.height = cell.height;
        shape.spotType = config.spotType;
        const isParkingCell =
          config.spotType === SpotShapeType.Parking ||
          config.spotType === SpotShapeType.ParkingSpace;
        if (isParkingCell) shape.spotAppearance = SpotShapeType.Node;
        shape.stateArea = {
          kind: "rect",
          hideDot: true,
          rect: {
            x: 0,
            y: 0,
            width: cell.width,
            height: cell.height,
            cornerRadius: 0
          }
        };
        shape.rectProps = {
          ...shape.rectProps,
          cornerRadius: 0,
          opacity: 0.35
        };
        return shape;
      })
    : [];

  state.addShapes([...lineShapes, ...spotShapes]);
  if (config.addSpots) state.setMeta({ lastSpotLabel: label });
  state.setChrome({ pendingGrid: null });
  state.setTool("select");
};
